import { useMemo } from "react";
import { useQuery } from "react-query";
import dashboardService from "services/DashBoardService";
import {
  buildTimelineFromStatsResponse,
  clampDateRangeToMaxDays,
  getDashboardStatsTimelineSpanDays,
  listDaysInclusive,
  MAX_DASHBOARD_TIMELINE_LINE_CHART_RANGE_DAYS,
  readFilterAppliedDateRange,
} from "./normalizeDashboardTimelineStats";

const EMPTY_RESULT = {
  mergedRows: [],
  isTimelineStatsRangeTooLong: false,
  filterAppliedDateRange: { startDate: "", endDate: "" },
};

// Cảnh báo vượt quá số ngày cho phép của line chart
export function useDashboardDailyTimelineStats({
  mode,
  baseParams,
  orderStatusList,
  enabled,
  dateRange,
  useDateRangeParams = false,
  isManualDateRange = false,
}) {
  const isDateRangeParamsEnabled = Boolean(
    useDateRangeParams && dateRange?.startDate && dateRange?.endDate
  );

  const dayList = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      return [];
    }
    return listDaysInclusive(dateRange.startDate, dateRange.endDate);
  }, [dateRange?.startDate, dateRange?.endDate]);

  const orderStatusKey = useMemo(
    () =>
      (orderStatusList || [])
        .map(statusItem => String(statusItem.value))
        .sort()
        .join(","),
    [orderStatusList]
  );

  const { data, isFetching, isLoading } = useQuery(
    [
      "dashboard-timeline-daily-bulk",
      mode,
      baseParams,
      isDateRangeParamsEnabled ? dateRange?.startDate : "",
      isDateRangeParamsEnabled ? dateRange?.endDate : "",
      orderStatusKey,
    ],
    async () => {
      if (
        isDateRangeParamsEnabled &&
        (!dateRange?.startDate || !dateRange?.endDate)
      ) {
        return EMPTY_RESULT;
      }
      const dayListInRange = isDateRangeParamsEnabled
        ? listDaysInclusive(dateRange.startDate, dateRange.endDate)
        : [];
      if (isDateRangeParamsEnabled && !dayListInRange.length) {
        return EMPTY_RESULT;
      }

      const params = {
        ...baseParams,
        ...(isDateRangeParamsEnabled
          ? {
            from_time: dateRange.startDate,
            to_time: dateRange.endDate,
          }
          : {}),
      };
      const rawResponse =
        mode === "dvvc"
          ? await dashboardService.statsByDvvc(params)
          : await dashboardService.statsByStatus(params);

      const filterAppliedDateRange = readFilterAppliedDateRange(rawResponse);

      const timelineRows = buildTimelineFromStatsResponse(
        rawResponse,
        mode,
        isDateRangeParamsEnabled ? { referenceRange: dateRange } : undefined
      );

      const resolveRangeTooLong = rowsForSpan => {
        const spanDays = getDashboardStatsTimelineSpanDays(
          rawResponse,
          rowsForSpan,
          isDateRangeParamsEnabled ? dateRange : undefined
        );
        return spanDays > MAX_DASHBOARD_TIMELINE_LINE_CHART_RANGE_DAYS;
      };

      if (!timelineRows.length) {
        return {
          mergedRows: [],
          isTimelineStatsRangeTooLong: resolveRangeTooLong([]),
          filterAppliedDateRange,
        };
      }

      if (!isDateRangeParamsEnabled) {
        const mergedRows = [...timelineRows].sort((a, b) => a.date.localeCompare(b.date));
        const isTimelineStatsRangeTooLong = resolveRangeTooLong(mergedRows);

        if (isTimelineStatsRangeTooLong && !isManualDateRange) {
          const displayDateRange = clampDateRangeToMaxDays(filterAppliedDateRange);
          const chartDayList = listDaysInclusive(
            displayDateRange.startDate,
            displayDateRange.endDate
          );
          const mergedRowsByDate = Object.fromEntries(
            mergedRows.map(rowItem => [rowItem.date, rowItem])
          );
          const clampedRows = chartDayList.map(dayIso => {
            const rowByDate = mergedRowsByDate[dayIso];
            if (rowByDate) {
              return rowByDate;
            }
            return {
              date: dayIso,
              statusCounts: {},
              carrierCounts: {},
            };
          });

          return {
            mergedRows: clampedRows,
            isTimelineStatsRangeTooLong: false,
            filterAppliedDateRange: displayDateRange,
          };
        }

        return {
          mergedRows: isTimelineStatsRangeTooLong ? [] : mergedRows,
          isTimelineStatsRangeTooLong,
          filterAppliedDateRange,
        };
      }

      const timelineByDate = Object.fromEntries(
        timelineRows.map(rowItem => [rowItem.date, rowItem])
      );
      const mergedRows = dayListInRange.map(dayIso => {
        const rowByDate = timelineByDate[dayIso];
        if (rowByDate) {
          return rowByDate;
        }
        return {
          date: dayIso,
          statusCounts: {},
          carrierCounts: {},
        };
      });

      const isTimelineStatsRangeTooLong = resolveRangeTooLong(mergedRows);
      return {
        mergedRows: isTimelineStatsRangeTooLong ? [] : mergedRows,
        isTimelineStatsRangeTooLong,
        filterAppliedDateRange,
      };
    },
    {
      enabled: Boolean(enabled && baseParams && baseParams.status),
      retry: 1,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const mergedRows = data?.mergedRows ?? EMPTY_RESULT.mergedRows;
  const isTimelineStatsRangeTooLong = Boolean(data?.isTimelineStatsRangeTooLong);
  const filterAppliedDateRange =
    data?.filterAppliedDateRange ?? EMPTY_RESULT.filterAppliedDateRange;

  return {
    mergedRows,
    isFetching,
    isLoading,
    dayList,
    isDateRangeParamsEnabled,
    isTimelineStatsRangeTooLong,
    filterAppliedDateRange,
  };
}
